/**
 * Starborn Auth - Client-side authentication library for Starborn-hosted sites
 *
 * This library provides authentication for sites hosted on Starborn.
 * It works with both preview subdomains (*.starborn.dev) and custom domains.
 *
 * Authentication Flow:
 * 1. User clicks sign in → redirected to Starborn login
 * 2. After login, Starborn creates a scoped third-party token (30 days)
 * 3. User is redirected back with the token in URL
 * 4. Token is stored in localStorage (persists across tabs and sessions)
 * 5. Token is silently refreshed when near expiry or after auth failure
 *
 * Usage:
 * 1. Include this script in your HTML: <script src="/__starborn/auth.js"></script>
 * 2. Use starbornAuth.onAuthStateChanged() to listen for auth state changes
 * 3. Use starbornAuth.getIdToken() to get the session token for API requests
 *
 * API Reference:
 *
 * starbornAuth.onAuthStateChanged(callback)
 *   Registers a callback that fires when auth state changes.
 *   Returns an unsubscribe function.
 *
 *   The callback receives an object with:
 *   - user: Object | null - User info if authenticated, null if signed out
 *     - uid: string - The user's unique ID
 *     - email: string | null - The user's email address
 *     - displayName: string | null - The user's display name (from Firebase provider)
 *     - photoURL: string | null - URL to the user's profile photo
 *     - emailVerified: boolean - Whether the email has been verified
 *     - getIdToken(): string - Returns the session token for API requests
 *   - idToken: string | null - Session token (named for backwards compat)
 *
 *   Example:
 *     const unsubscribe = starbornAuth.onAuthStateChanged(({ user }) => {
 *       if (user) {
 *         console.log('Logged in as:', user.email);
 *         console.log('User ID:', user.uid);
 *       } else {
 *         console.log('Not logged in');
 *       }
 *     });
 *
 * starbornAuth.getIdToken()
 *   Returns the session token or null if not authenticated.
 *   Tokens are valid for 30 days and refreshed automatically when near expiry.
 *   Use this for API requests: { Authorization: `Bearer ${token}` }
 *
 * starbornAuth.currentUser
 *   The current user object, or null if not authenticated.
 *   Same structure as the user object passed to onAuthStateChanged.
 *
 * starbornAuth.isAuthenticated()
 *   Returns true if user is currently authenticated.
 *
 * starbornAuth.signIn()
 *   Redirects to Starborn login page. User returns to current page after login.
 *
 * starbornAuth.signOut()
 *   Signs out the current user and clears stored credentials.
 *
 * starbornAuth.helpers.fetch(url, options)
 *   Wrapper around fetch() that automatically includes Authorization and X-Sb-Metadata.
 *   Relative paths are sent to the API base (https://varoon.com in production).
 *   Kicks off a background refresh if the token is near expiry; redirects to
 *   interactive login on 401.
 *
 * starbornAuth.helpers.getApiBase()
 *   Returns the API base URL (e.g. https://varoon.com) for building absolute URLs.
 *
 * starbornAuth.helpers.getProjectId()
 *   Returns the project ID for the currently loaded page (from __sb_metadata__.js).
 *   Returns null if metadata hasn't loaded or this isn't a Starborn-published page.
 *
 * starbornAuth.helpers.requireAuth()
 *   Returns session token or redirects to login if not authenticated.
 *
 * starbornAuth.helpers.getProjectRole(projectId?)
 *   Returns the user's role in the project ("admin", "editor", "viewer", or "").
 *   If projectId is omitted, it is read from __sb_metadata__.js (payload field p).
 *   Results are cached for the page session. Returns null if not authenticated.
 */

(function() {
  'use strict';

  // Idempotency: this script may be loaded twice — once injected by the preview
  // infrastructure (backend/SW) and once explicitly by the user's app. Skip
  // re-initialization so we don't clobber listeners or double-fetch metadata.
  if (window.starbornAuth) return;

  // Metadata token for version-pinned permission checks.
  // Loaded from virtual file __sb_metadata__.js (bypasses SW, cached per-version by CF).
  // Relative URL so it resolves correctly under any path prefix (e.g. /t/{token}/).
  let _metadataToken = null;
  let _metadataProjectId = null;
  function _loadMetadata() {
    return fetch('__sb_metadata__.js')
      .then(function(r) { return r.ok ? r.text() : ''; })
      .then(function(text) {
        var m = text.match(/"([^"]+)"/);
        if (m) {
          _metadataToken = m[1];
          // Decode payload to extract the real project ID (format: base64url_payload.signature)
          try {
            var dotIdx = _metadataToken.indexOf('.');
            if (dotIdx > 0) {
              var encoded = _metadataToken.slice(0, dotIdx).replace(/-/g, '+').replace(/_/g, '/');
              var payload = JSON.parse(atob(encoded));
              if (payload && payload.p) _metadataProjectId = payload.p;
            }
          } catch { /* ignore decode errors */ }
        }
      })
      .catch(function() {});
  }
  _loadMetadata(); // Fire immediately

  // Determine auth domain based on current environment
  // For local dev (*.localtest.me), use the same origin
  // For production (*.starborn.dev or custom domains), use starborn.app
  const getAuthDomain = () => {
    const hostname = window.location.hostname;

    // Local development: use same origin
    if (hostname.endsWith('.localtest.me') || hostname === 'localhost') {
      return window.location.origin;
    }

    // Production: use starborn.app
    return 'https://varoon.com';
  };

  // API base URL for published-site API calls (forms, email, AI). Traffic goes directly to API.
  // Local dev: use same origin so requests hit local backend; production: api.starborn.app
  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname.endsWith('.localtest.me') || hostname === 'localhost') {
      return window.location.origin;
    }
    return 'https://varoon.com';
  };

  const AUTH_DOMAIN = getAuthDomain();
  const STORAGE_KEY = '__starborn_dev_auth_session_token';
  const REFRESH_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // refresh when <7 days until expiry

  // Per-tab random jitter (0–6 h) so multiple tabs don't all refresh at once.
  const REFRESH_JITTER_MS = Math.random() * 6 * 60 * 60 * 1000;

  // Debug: Log version and auth domain
  console.log('[Starborn Auth v2024-12-10] Loading with AUTH_DOMAIN:', AUTH_DOMAIN);

  /**
   * Decode a token and extract the payload
   * Supports both standard JWT (3 parts: header.payload.signature)
   * and Starborn third-party tokens (2 parts: payload.signature)
   * @param {string} token - The token to decode
   * @returns {object|null} - The decoded payload or null if invalid
   */
  function decodeJwt(token) {
    try {
      const parts = token.split('.');
      // Support both 3-part JWT and 2-part Starborn tokens
      if (parts.length !== 3 && parts.length !== 2) return null;

      // For 3-part JWT, payload is parts[1]; for 2-part, payload is parts[0]
      const payload = parts.length === 3 ? parts[1] : parts[0];

      // Handle base64url encoding (replace - with + and _ with /)
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.warn('[Starborn Auth] Failed to decode token:', err);
      return null;
    }
  }

  /**
   * Create a User object from a decoded token payload
   * Supports both Firebase JWT and Starborn third-party token formats
   * @param {string} idToken - The raw token
   * @param {object} payload - The decoded payload
   * @returns {object} - User object with common properties
   */
  function createUser(idToken, payload) {
    return {
      // Core identifiers - support both Firebase (user_id/sub) and Starborn (uid) formats
      uid: payload.uid || payload.user_id || payload.sub,
      // Email: explicit field, or derived from guest UID (guest:email@example.com)
      email: payload.email || ((payload.uid || '').startsWith('guest:') ? (payload.uid || '').slice(6) : null),
      emailVerified: payload.email_verified || false,

      // Profile info (from Firebase provider data, may not exist in Starborn tokens)
      displayName: payload.name || null,
      photoURL: payload.picture || null,

      // Provider info
      providerId: payload.firebase?.sign_in_provider || 'starborn',

      // Token info
      isAnonymous: payload.firebase?.sign_in_provider === 'anonymous',

      // Starborn-specific: token scope (e.g., ["forms:read", "forms:write"])
      scope: payload.scope || null,

      // Method to get the token (matches Firebase Auth API)
      getIdToken: () => idToken,

      // Raw token data for debugging
      _tokenPayload: payload
    };
  }

  class StarbornAuth {
    constructor() {
      this.idToken = null;     // Stores third-party token (name kept for backwards compat)
      this.currentUser = null;
      this.listeners = [];

      console.log('[Starborn Auth] Constructor called, window.location.href:', window.location.href);

      // Try to restore token from sessionStorage
      this.restoreToken();

      // Handle auth callback on page load (token in URL)
      this.handleAuthCallback();
    }

    /**
     * Restore token from localStorage (persists across tabs and sessions)
     */
    restoreToken() {
      console.log('[Starborn Auth] Restoring token from localStorage');
      try {
        const token = localStorage.getItem(STORAGE_KEY);
        if (token) {
          this.setToken(token);
        }
      } catch (err) {
        console.warn('[Starborn Auth] Could not restore token:', err);
      }
    }

    /**
     * Set the token and create/update the user object
     * @param {string|null} token - The ID token or null to clear
     */
    setToken(token) {
      this.idToken = token;
      if (token) {
        const payload = decodeJwt(token);
        if (payload) {
          this.currentUser = createUser(token, payload);
          console.log('[Starborn Auth] User set:', this.currentUser.uid, this.currentUser.email);
        } else {
          // Token couldn't be decoded, but still store it
          this.currentUser = null;
          console.warn('[Starborn Auth] Token stored but could not decode user info');
        }
      } else {
        this.currentUser = null;
      }
    }

    /**
     * Save token to localStorage
     */
    saveToken(token) {
      console.log('[Starborn Auth] Saving token to localStorage');
      try {
        if (token) {
          localStorage.setItem(STORAGE_KEY, token);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (err) {
        console.warn('[Starborn Auth] Could not save token:', err);
      }
    }

    /**
     * Handle the auth callback from login redirect
     * Receives a scoped third-party token (30 days, forms API only)
     */
    handleAuthCallback() {
      const url = new URL(window.location.href);
      const thirdPartyToken = url.searchParams.get('thirdPartyToken');
      // Also check for legacy idToken param for backwards compatibility
      const legacyIdToken = url.searchParams.get('idToken');

      const token = thirdPartyToken || legacyIdToken;
      const tokenType = thirdPartyToken ? 'thirdPartyToken' : 'idToken';

      console.log('[Starborn Auth] handleAuthCallback -', tokenType, 'found:', !!token);
      if (token) {
        console.log('[Starborn Auth] Storing', tokenType);

        // Remove token from URL immediately for security
        url.searchParams.delete('thirdPartyToken');
        url.searchParams.delete('idToken');
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);

        // Store token directly (no exchange needed for third-party tokens)
        this.setToken(token);
        this.saveToken(token);

        // Notify listeners
        this.notifyListeners();
        console.log('[Starborn Auth] Token stored successfully');
      }
    }

    /**
     * Get the current user's ID token
     * Returns null if user is not authenticated
     */
    getIdToken() {
      console.log('[Starborn Auth] getIdToken called, token present:', !!this.idToken);
      return this.idToken;
    }

    /**
     * Check if user is currently authenticated
     */
    isAuthenticated() {
      console.log('[Starborn Auth] isAuthenticated check:', !!this.idToken);
      return this.idToken !== null;
    }

    /**
     * Sign in by redirecting to Starborn login page
     * The user will be redirected back to the current page after login
     * with a scoped third-party token (30 days, forms API only)
     */
    signIn() {
      console.log('[Starborn Auth] Initiating sign-in process');
      const redirectUrl = encodeURIComponent(window.location.href);
      const loginUrl = `${AUTH_DOMAIN}/login?redirectWithThirdPartyToken=${redirectUrl}`;
      console.log('[Starborn Auth] Redirecting to login:', loginUrl);
      window.location.href = loginUrl;
    }

    /**
     * Sign out the current user
     */
    signOut() {
      console.log('[Starborn Auth] Signing out user');
      this.setToken(null);
      this.saveToken(null);
      this.notifyListeners();
    }

    /**
     * Check whether the current token is expired or will expire within REFRESH_THRESHOLD_MS.
     * @returns {boolean}
     */
    isTokenExpiringSoon() {
      if (!this.idToken) return false;
      const payload = decodeJwt(this.idToken);
      if (!payload?.exp) return false;

      const remainingMs = payload.exp * 1000 - Date.now();
      return remainingMs < (REFRESH_THRESHOLD_MS + REFRESH_JITTER_MS);
    }

    /**
     * Silently refresh the third-party token by presenting the current (still-valid)
     * token as a Bearer credential. No cross-site cookies required.
     * Uses single-flight dedupe so concurrent callers share one request.
     * @returns {Promise<string|null>} The new token, or null on failure
     */
    refreshToken() {
      if (this._refreshPromise) return this._refreshPromise;

      this._refreshPromise = (async () => {
        try {
          if (!this.idToken) return null;
          console.log('[Starborn Auth] Attempting silent token refresh');
          const resp = await fetch(`${getApiBase()}/api/auth/third-party-token`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${this.idToken}` },
          });
          if (!resp.ok) {
            console.warn('[Starborn Auth] Silent refresh failed:', resp.status);
            return null;
          }
          const data = await resp.json();
          const token = data.token;
          if (token) {
            this.setToken(token);
            this.saveToken(token);
            console.log('[Starborn Auth] Token refreshed silently');
          }
          return token;
        } catch (err) {
          console.warn('[Starborn Auth] Silent refresh error:', err);
          return null;
        } finally {
          this._refreshPromise = null;
        }
      })();

      return this._refreshPromise;
    }

    /**
     * Add an authentication state listener
     * Callback receives { user, idToken } where user is a Firebase-like User object
     * @param {function} callback - Function called with { user, idToken }
     * @returns {function} Unsubscribe function
     */
    onAuthStateChanged(callback) {
      this.listeners.push(callback);

      // Immediately call with current state
      callback(this.getAuthState());

      // Return unsubscribe function
      return () => {
        const index = this.listeners.indexOf(callback);
        if (index > -1) {
          this.listeners.splice(index, 1);
        }
      };
    }

    /**
     * Get the current auth state object
     * @returns {{ user: object|null, idToken: string|null }}
     */
    getAuthState() {
      return {
        user: this.currentUser,
        idToken: this.idToken
      };
    }

    /**
     * Notify all listeners of auth state change
     */
    notifyListeners() {
      const state = this.getAuthState();
      console.log('[Starborn Auth] Notifying listeners of auth state change:',
        state.user ? `user=${state.user.uid}` : 'signed out');
      this.listeners.forEach(callback => {
        try {
          callback(state);
        } catch (err) {
          console.error('[Starborn Auth] Listener error:', err);
        }
      });
    }
  }

  // Create global instance
  window.starbornAuth = new StarbornAuth();

  // Before refreshing, check if another tab already refreshed the token in
  // localStorage. This avoids redundant refresh calls across tabs without any
  // cross-tab messaging. Each tab uses a small random jitter so they don't all
  // hit the refresh endpoint simultaneously.
  const _origRefreshToken = window.starbornAuth.refreshToken.bind(window.starbornAuth);
  window.starbornAuth.refreshToken = function () {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored !== this.idToken) {
        const payload = decodeJwt(stored);
        if (payload?.exp && (payload.exp * 1000 - Date.now()) > REFRESH_THRESHOLD_MS) {
          console.log('[Starborn Auth] Another tab already refreshed, using stored token');
          this.setToken(stored);
          return Promise.resolve(stored);
        }
      }
    } catch { /* ignore localStorage errors */ }
    return _origRefreshToken();
  };

  // Expose a simpler API for common use cases
  window.starbornAuth.helpers = {
    /**
     * Make an authenticated API request to Starborn.
     * Automatically includes the session token and X-Sb-Metadata (from __sb_metadata__.js).
     * Relative paths are resolved against the API base (https://varoon.com in production).
     * Kicks off a background refresh if the token is near expiry; redirects to
     * interactive login on 401.
     *
     * @param {string} url - API path (e.g., '/api/forms/contact/submissions') or full URL
     * @param {object} options - fetch options
     * @returns {Promise<Response>}
     */
    async fetch(url, options = {}) {
      const auth = window.starbornAuth;

      // Pre-request: if token is near expiry, kick off a background refresh.
      // We don't await — the current token is still valid, so this request
      // proceeds immediately and future requests pick up the refreshed token.
      if (auth.idToken && auth.isTokenExpiringSoon()) {
        auth.refreshToken();
      }

      const buildHeaders = () => {
        const headers = { ...options.headers };
        const token = auth.getIdToken();
        if (token && token.length >= 8) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (_metadataToken) {
          headers['X-Sb-Metadata'] = _metadataToken;
        }
        return headers;
      };

      if (!_metadataToken) await _loadMetadata();

      const fullUrl = getApiBase() + url;
      const resp = await fetch(fullUrl, { ...options, headers: buildHeaders() });

      // Post-request: on 401 the token is invalid/expired and cannot be
      // refreshed — redirect to interactive login. Note: 403 is NOT treated
      // as an auth failure; it means the user is authenticated but lacks
      // permission for this resource. Redirecting on 403 would cause an
      // infinite loop since re-authentication yields the same token.
      if (resp.status === 401 && auth.idToken) {
        console.warn('[Starborn Auth] Auth failed (', resp.status, '), redirecting to login');
        auth.signIn();
      }

      return resp;
    },

    /**
     * Base URL for the Starborn API (e.g. https://varoon.com). Use for building
     * absolute URLs when needed (e.g. anonymous form submit).
     */
    getApiBase() {
      return getApiBase();
    },

    /**
     * Returns the project ID for the currently loaded page, decoded from
     * __sb_metadata__.js. Returns null if metadata hasn't loaded yet or the
     * page isn't a Starborn-published app.
     *
     * @returns {Promise<string|null>}
     */
    async getProjectId() {
      if (!_metadataProjectId) await _loadMetadata();
      return _metadataProjectId || null;
    },

    /**
     * Get session token or redirect to login
     * Use this when authentication is required
     */
    requireAuth() {
      const sessionToken = window.starbornAuth.getIdToken();
      if (!sessionToken) {
        window.starbornAuth.signIn();
        return null;
      }
      return sessionToken;
    },

    /**
     * Get the current user's role in the project being viewed.
     * Returns the role string ("admin", "editor", "viewer") or "" if no role.
     * Returns null if the user is not authenticated.
     * Results are cached for the session.
     *
     * @param {string} [projectId] - Project ID. If omitted, taken from __sb_metadata__.js (decoded payload.p).
     * @returns {Promise<string|null>}
     */
    async getProjectRole(projectId) {
      if (!window.starbornAuth.isAuthenticated()) return null;

      if (!projectId) {
        if (!_metadataProjectId) await _loadMetadata();
        projectId = _metadataProjectId;
      }
      if (!projectId) return null;

      const uid = window.starbornAuth.currentUser?.uid || '';
      const storageKey = `__starborn_project_role_${projectId}_${uid}`;

      // Helper to fetch and update cache in the background
      const revalidate = () => {
        window.starbornAuth.helpers.fetch(`/api/public/projects/${projectId}/my-role`)
          .then(resp => resp.ok ? resp.json() : { role: '' })
          .then(data => {
            const role = data.role || '';
            const hadRole = window[storageKey];
            window[storageKey] = role;
            try { localStorage.setItem(storageKey, JSON.stringify({ role, exp: Date.now() + 3600000 })); } catch { /* ignore */ }
            // If we got admin/editor and didn't have it before, notify listeners so they can re-check
            // (fixes stale cache after permissions change — e.g. Starborn admins on samples now get admin)
            if ((role === 'admin' || role === 'editor') && role !== hadRole) {
              window.starbornAuth.notifyListeners();
            }
          })
          .catch(() => {});
      };

      // Check in-memory cache (instant)
      if (window[storageKey] !== undefined) return window[storageKey];

      // Check localStorage (stale-while-revalidate: return cached, refresh in background)
      try {
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          const { role, exp } = JSON.parse(cached);
          window[storageKey] = role;
          // Revalidate when stale, or when cached role is not admin (may have upgraded — e.g. editor→admin, Starborn admin on sample)
          if (Date.now() >= exp || role !== 'admin') revalidate();
          return role;
        }
      } catch { /* ignore */ }

      // No cache at all — must await the fetch
      try {
        const resp = await window.starbornAuth.helpers.fetch(`/api/public/projects/${projectId}/my-role`);
        if (!resp.ok) {
          const body = await resp.text().catch(() => '');
          console.warn(`[Starborn Auth] my-role failed for project ${projectId}: HTTP ${resp.status}`, body);
          window[storageKey] = '';
          return '';
        }
        const data = await resp.json();
        const role = data.role || '';
        window[storageKey] = role;
        try { localStorage.setItem(storageKey, JSON.stringify({ role, exp: Date.now() + 3600000 })); } catch { /* ignore */ }
        return role;
      } catch (err) {
        console.warn('[Starborn Auth] Failed to get project role:', err);
        return '';
      }
    }
  };

  console.log('[Starborn Auth] Initialized', window.starbornAuth.isAuthenticated() ? '(authenticated)' : '(not authenticated)');
})();
